import { API_URL, authenticatedFetch, parseApiErrorMessage } from "../config/api";

const rethrow = (e: unknown): never => {
  if (e instanceof Error) throw e;
  throw new Error(String(e));
};

export const getProjectMeetingNotes = async (_token: string, project_id: string, options?: { archived?: boolean; page?: number; size?: number }): Promise<ProjectMeetingNotesInterface> => {
  try {
    const url = new URL(`${API_URL}/projects/${project_id}/meeting-notes`);
    const size = options?.size ?? 1000;
    const page = options?.page ?? 1;
    url.searchParams.set('size', String(size));
    url.searchParams.set('page', String(page));
    if (options?.archived) {
      url.searchParams.set('archived', 'true');
    }
    const r = await authenticatedFetch(url.toString());
    const data:ProjectMeetingNotesInterface = await r.json();

    console.log('📥 API GET meeting notes response details:', {
      total: data.total,
      resultsCount: data.results?.length,
      allFieldsInFirstItem: data.results?.[0] ? Object.keys(data.results[0]) : [],
      allRecordsWithFields: data.results?.map((item, index) => ({
        index,
        id: item.id,
        hasDate: 'date' in item,
        dateValue: item.date,
        allFields: Object.keys(item),
        customers: Array.isArray(item.customers) ? `Array(${item.customers.length})` : typeof item.customers,
        vendors: Array.isArray(item.vendors) ? `Array(${item.vendors.length})` : typeof item.vendors
      }))
    });

    return data;
  } catch(e) {
    return rethrow(e);
  }
};

export const getProjectMeetingNoteById = async (_token: string, project_id: string, item_id: string): Promise<ProjectMeetingNote> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/projects/${project_id}/meeting-notes/${item_id}`);
    const data:ProjectMeetingNote = await r.json();

    return data;
  } catch(e) {
    return rethrow(e);
  }
}

export const createProjectMeetingNote = async (_token: string, project_id: string, data: ProjectMeetingNote): Promise<ProjectMeetingNote> => {
  try {
    // Sanitize data: convert empty strings to null for optional UUID fields
    const sanitizedData: any = { ...data };
    if (!sanitizedData.meeting_type_id) {
      delete sanitizedData.meeting_type_id;
    }

    console.log('🔄 API POST request data:', JSON.stringify(sanitizedData, null, 2));
    const r = await authenticatedFetch(`${API_URL}/projects/${project_id}/meeting-notes`,{
      method: 'POST',
      body: JSON.stringify(sanitizedData)
    })

    console.log('📤 API POST response status:', r.status, r.statusText);

    if (!r.ok) {
      const message = await parseApiErrorMessage(r, 'Failed to save meeting note');
      console.error('❌ API POST error response:', message);
      throw new Error(message);
    }
    const res:any = await r.json();
    console.log('📥 API POST response data:', res);

    return res;
  } catch(e) {
    console.error('🚨 API POST error:', e);
    return rethrow(e);
  }
}

export const updateProjectMeetingNotes = async (_token: string, project_id: string, item_id: string, data: ProjectMeetingNote): Promise<any> => {
  try {
    // Sanitize data: convert empty strings to null for optional UUID fields
    const sanitizedData: any = { ...data };
    if (!sanitizedData.meeting_type_id) {
      delete sanitizedData.meeting_type_id;
    }

    console.log('🔄 API PUT request data:', JSON.stringify(sanitizedData, null, 2));
    const res = await authenticatedFetch(`${API_URL}/projects/${project_id}/meeting-notes/${item_id}`,{
      method: 'PUT',
      body: JSON.stringify(sanitizedData)
    })

    console.log('📤 API PUT response status:', res.status, res.statusText);

    if (!res.ok) {
      const message = await parseApiErrorMessage(res, 'Failed to save meeting note');
      console.error('❌ API PUT error response:', message);
      throw new Error(message);
    }

    // Try to parse response body if available
    const contentType = res.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const responseData = await res.json();
      console.log('📥 API PUT response data:', responseData);
      return responseData;
    }

    console.log('✅ API PUT success (no response data)');
    return res;
  } catch(e) {
    console.error('🚨 API PUT error:', e);
    return rethrow(e);
  }
};

export const deleteProjectMeetingNotes = async (_token: string, project_id: string, item_id: string): Promise<any> => {
  try {
    const res = await authenticatedFetch(`${API_URL}/projects/${project_id}/meeting-notes/${item_id}`,{
      method: 'DELETE'
    })

    if (!res.ok) {
      const message = await parseApiErrorMessage(res, 'Failed to delete meeting note');
      throw new Error(message);
    }

    return res;
  } catch(e) {
    return rethrow(e);
  }
};

export const getProjectMeetingNoteTypes = async (_token: string): Promise<ProjectMeetingNoteTypesPageInterface> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/projects/meeting-types`);
    const data:ProjectMeetingNoteTypesPageInterface = await r.json();

    return data;
  } catch(e) {
    return rethrow(e);
  }
};

export const createProjectMeetingNoteTypes = async (_token: string, dataObj: ProjectMeetingNoteTypesInterface): Promise<{id: string}> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/projects/meeting-types`, {
      method: 'POST',
      body: JSON.stringify(dataObj)
    })
    const data:any = await r.json();

    return data;
  } catch(e) {
    return rethrow(e);
  }
};

export const getProjectMeetingNoteTypeById = async (_token: string, meeting_type_id: string): Promise<ProjectMeetingNoteTypesInterface> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/projects/meeting-types/${meeting_type_id}`);
    const data:ProjectMeetingNoteTypesInterface = await r.json();

    return data;
  } catch(e) {
    return rethrow(e);
  }
};

export const updateProjectMeetingNoteType = async (_token: string, meeting_type_id: string, dataObj: ProjectMeetingNoteTypesInterface): Promise<ProjectMeetingNoteTypesInterface> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/projects/meeting-types/${meeting_type_id}`, {
      method: 'PUT',
      body: JSON.stringify(dataObj)
    });

    if (!r.ok) {
      const message = await parseApiErrorMessage(r, 'Failed to update meeting type');
      throw new Error(message);
    }

    // Some APIs return 204 No Content on successful update
    const contentType = r.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const data: ProjectMeetingNoteTypesInterface = await r.json();
      return data;
    }

    // If no JSON response, return the original data
    return dataObj;
  } catch(e) {
    return rethrow(e);
  }
};

export const deleteProjectMeetingNoteType = async (_token: string, meeting_type_id: string): Promise<any> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/projects/meeting-types/${meeting_type_id}`, {
      method: 'DELETE'
    })

    if (!r.ok) {
      const message = await parseApiErrorMessage(r, 'Failed to delete meeting type');
      throw new Error(message);
    }

    return r;
  } catch(e) {
    return rethrow(e);
  }
};
